/**
 * Aritmética de dinheiro em centavos inteiros (D23 do PRD do MVP 1).
 *
 * 🔴 RISCO CRÍTICO. Prisma sobre MongoDB não tem `Decimal`, então todo valor monetário é
 * `Float` no banco — e `Float` não fecha conta: `19.99 * 3` é `59.97000000000001` e
 * `0.1 + 0.2` é `0.30000000000000004`. O critério de aceite da F6 exige que o total exibido
 * na confirmação seja **bit-a-bit** o total persistido, e duas somas independentes em double
 * (uma na tela, uma no servidor) divergem no último centavo.
 *
 * A regra: converta para centavos na fronteira, **faça toda a conta em `Int`**, e volte para
 * `Float` só no momento de gravar ou exibir. Comparação entre totais é sempre entre centavos —
 * nunca `===` entre dois `Float`.
 *
 * Este é o helper ÚNICO do monorepo (D16): o backend calcula o total autoritativo com ele e o
 * frontend monta a prévia informativa do carrinho com ele. Duas implementações seriam duas
 * verdades sobre o valor que o hóspede paga.
 */

/**
 * Converte um valor monetário para centavos inteiros.
 *
 * O `Math.round` é o ponto todo: `24.9 * 100` é `2489.9999999999995` em double, e um `Math.trunc`
 * ou um `parseInt` transformariam R$ 24,90 em R$ 24,89 — um centavo a menos por item, silencioso.
 *
 * Valor não finito (`NaN`, `Infinity`, `null`, `undefined`) devolve `0` em vez de propagar `NaN`
 * pela soma: `NaN` contamina o total inteiro e chega ao banco como total nulo. Quem chama é
 * responsável por validar o dado antes — aqui a degradação é para zero, nunca para lixo.
 */
export function toCents(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 100);
}

/**
 * Converte centavos inteiros de volta para o valor monetário de 2 casas gravado no banco.
 *
 * A divisão por 100 pode produzir representação inexata (`5997 / 100` é exato, mas nem todo
 * valor é), por isso o resultado passa por `Math.round(x * 100) / 100`: o que sai daqui é sempre
 * um `Float` que representa um número exato de centavos, e é ele que vai para o Prisma.
 */
export function fromCents(cents: number): number {
  if (!Number.isFinite(cents)) {
    return 0;
  }
  return Math.round(cents) / 100;
}

/**
 * Multiplica um valor monetário por uma quantidade, devolvendo centavos inteiros.
 *
 * `quantity` é arredondado porque `OrderProduct.quantity` é `Float` no schema (o PDV permite
 * fração), mas o pedido do cardápio valida `@IsInt()` no DTO: fração aqui reabriria justamente
 * o arredondamento que este helper existe para eliminar.
 */
export function multiplyCents(unitValue: number | null | undefined, quantity: number): number {
  if (!Number.isFinite(quantity)) {
    return 0;
  }
  return toCents(unitValue) * Math.round(quantity);
}

/** Soma parcelas em centavos. Inteiro + inteiro é exato: é por isso que a soma acontece aqui. */
export function sumCents(values: number[]): number {
  return values.reduce((total, value) => total + (Number.isFinite(value) ? Math.round(value) : 0), 0);
}

/**
 * M2-D77 — preço da **unidade montada**, em centavos: o produto mais cada adicional escolhido.
 *
 * É o `unitPrice` do item do pedido, e é o que o integrador multiplica pela quantidade para lançar:
 * `unitPrice × quantity = totalPrice` continua valendo com adicional pago. Recebe os preços em reais,
 * como vêm do banco, e converte cada parcela **antes** de somar: `24.9 + 4.1 + 0.07` somado em `Float`
 * dá `29.07` por sorte, e a mesma soma vezes 3 já sai `87.21000000000001`.
 *
 * Adicional grátis é `0` (ou ausente da lista), e sem adicional o resultado é `toCents(basePrice)`: todo
 * pedido anterior à M2-F4 fecha no mesmo centavo de antes.
 *
 * ⚠️ Não valida o preço. Valor ilegível degrada para `0` como em `toCents` — e na fronteira isso seria
 * adicional grátis em silêncio. Quem lê o preço do banco o passa pela guarda de leitura antes
 * (M2-D87: preço não finito, negativo ou fora dos centavos exatos fecha a opção), e quem grava confere
 * `Number.isSafeInteger` no resultado.
 */
export function assembledUnitCents(basePrice: number | null | undefined, optionPrices: readonly (number | null | undefined)[]): number {
  return sumCents([toCents(basePrice), ...optionPrices.map((price) => toCents(price))]);
}

/**
 * M2-D77 — total da **linha** em centavos: a unidade montada vezes a quantidade.
 *
 * Diferente de `multiplyCents`, recebe **centavos**, não reais: a unidade montada já é soma de
 * parcelas convertidas, e voltar para reais só para multiplicar reabriria o arredondamento. Com a
 * unidade de um produto sem adicional, dá exatamente o `multiplyCents` do preço — é a mesma conta.
 *
 * `quantity` é arredondado pelo mesmo motivo do `multiplyCents`. Entrada não finita devolve `0`.
 */
export function lineTotalCents(unitCents: number, quantity: number): number {
  if (!Number.isFinite(unitCents) || !Number.isFinite(quantity)) {
    return 0;
  }
  return Math.round(unitCents) * Math.round(quantity);
}
